import React, { useEffect, useState } from 'react';
import {
  Activity, AlertTriangle, RefreshCw, Users, CheckCircle2, Clock,
  Siren, QrCode, Download, Settings2, UserPlus, Megaphone, XCircle,
} from 'lucide-react';
import { apiCall, downloadFile } from '../../utils/api';
import { toast } from '../ui/sonner';
import { useLiveRefresh } from '../../hooks/useLiveRefresh';
import { SkeletonRows, EmptyState } from './OrgPanelShared';

const BADGE_STYLES = {
  gold: 'bg-amber-100 text-amber-800 border border-amber-300',
  silver: 'bg-slate-200 text-slate-700 border border-slate-300',
  bronze: 'bg-orange-100 text-orange-800 border border-orange-300',
};

/** Attendance & Rescue center (shared employer + agency):
 * Live Board | Rescue Center | Analytics | Settings. */
const SUB = [
  { id: 'live', label: 'Live Board', icon: Activity },
  { id: 'rescue', label: 'Rescue Center', icon: Siren },
  { id: 'analytics', label: 'Analytics', icon: CheckCircle2 },
  { id: 'settings', label: 'Settings', icon: Settings2 },
];

const STATE_BADGE = {
  late: 'bg-amber-100 text-amber-700',
  at_risk: 'bg-orange-100 text-orange-700',
  no_show: 'bg-red-100 text-red-700',
  recovered: 'bg-emerald-100 text-emerald-700',
};

export default function AttendanceRescuePanel() {
  const [sub, setSub] = useState('live');
  return (
    <div className="space-y-4" data-testid="attendance-rescue-panel">
      <div className="flex items-center gap-1 flex-wrap">
        {SUB.map((s) => {
          const Icon = s.icon;
          return (
            <button key={s.id} onClick={() => setSub(s.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${sub === s.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`attendance-sub-${s.id}`}>
              <Icon className="w-3.5 h-3.5" /> {s.label}
            </button>
          );
        })}
      </div>
      {sub === 'live' && <LiveBoard />}
      {sub === 'rescue' && <RescueCenter />}
      {sub === 'analytics' && <AttendanceAnalytics />}
      {sub === 'settings' && <AttendanceSettings />}
    </div>
  );
}

function LiveBoard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrFor, setQrFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setRows((await apiCall('/api/org/attendance/live')).rows || []); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useLiveRefresh(['attendance', 'jobs'], () => {
    apiCall('/api/org/attendance/live').then((r) => setRows(r.rows || [])).catch(() => {});
  });

  const showQr = async (row) => {
    try {
      const res = await apiCall(`/api/org/shifts/${row.job_id}/checkin-token`);
      setQrFor({ ...row, ...res });
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-slate-900 text-sm">Today&apos;s shift attendance</h4>
        <button onClick={load} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900" data-testid="live-board-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      {loading ? <SkeletonRows /> : rows.length === 0 ? <EmptyState label="No assigned shifts in the current window." /> : (
        <ul className="divide-y divide-slate-100 bg-white border border-slate-200 rounded-xl" data-testid="live-board-list">
          {rows.map((r) => (
            <li key={r.job_id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="font-medium text-slate-900 truncate">{r.title}</p>
                <p className="text-xs text-slate-500">
                  {String(r.start_time).slice(0, 16).replace('T', ' ')} · {r.worker_name || r.worker_id}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {r.checked_out ? (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">Completed</span>
                ) : r.on_break ? (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-700">On break</span>
                ) : r.checked_in ? (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700">
                    Clocked in{r.late ? ' (late)' : ''}{r.method && r.method !== 'gps' ? ` · ${r.method.toUpperCase()}` : ''}
                  </span>
                ) : (
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATE_BADGE[r.attendance_state] || 'bg-slate-100 text-slate-500'}`}>
                    {r.attendance_state ? r.attendance_state.replace('_', ' ') : 'awaiting clock-in'}
                  </span>
                )}
                {r.rescue_active && <Siren className="w-4 h-4 text-red-500" title="Shift Rescue active" />}
                <button onClick={() => showQr(r)} className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                  title="Show check-in QR / NFC code" data-testid={`live-qr-${r.job_id}`}>
                  <QrCode className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {qrFor && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setQrFor(null)} data-testid="checkin-qr-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-5 space-y-3 text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-slate-900">Check-in code — {qrFor.title}</h3>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(qrFor.qr_payload)}`}
              alt="Check-in QR" className="mx-auto rounded-lg border border-slate-200" width={220} height={220} />
            <p className="text-[11px] text-slate-500 break-all">Site code (type or NFC-write): <span className="font-mono text-slate-800">{qrFor.token}</span></p>
            <p className="text-xs text-slate-500">Print/display this QR at the worksite, or write the code to an NFC tag. Workers scanning it are presence-verified. Valid 24h.</p>
            <button onClick={() => setQrFor(null)} className="w-full py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700" data-testid="qr-modal-close">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function RescueCenter() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replFor, setReplFor] = useState(null);
  const [repls, setRepls] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setShifts((await apiCall('/api/org/rescue/active')).shifts || []); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useLiveRefresh(['attendance', 'jobs'], () => {
    apiCall('/api/org/rescue/active').then((r) => setShifts(r.shifts || [])).catch(() => {});
  });

  const findReplacements = async (s) => {
    setReplFor(s); setRepls([]); setBusy(true);
    try { setRepls((await apiCall(`/api/org/shifts/${s._id}/rescue/replacements`, { method: 'POST' })).replacements || []); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const assign = async (workerId) => {
    try {
      await apiCall(`/api/org/shifts/${replFor._id}/rescue/assign`, { method: 'POST', body: JSON.stringify({ worker_id: workerId }) });
      toast.success('Replacement assigned and notified');
      setReplFor(null); load();
    } catch (e) { toast.error(e.message); }
  };

  const openEmergency = async (s) => {
    try {
      const res = await apiCall(`/api/org/shifts/${s._id}/rescue/open-emergency`, { method: 'POST' });
      toast.success(`Open Emergency Shift published — ${res.top_matches_notified} top matches notified`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const resolve = async (s) => {
    try { await apiCall(`/api/org/shifts/${s._id}/rescue/resolve`, { method: 'POST' }); toast.success('Rescue resolved'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-slate-900 text-sm flex items-center gap-1.5"><Siren className="w-4 h-4 text-red-500" /> Shifts needing rescue</h4>
        <button onClick={load} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900" data-testid="rescue-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      {loading ? <SkeletonRows /> : shifts.length === 0 ? <EmptyState label="No at-risk shifts — everything is covered. 🎉" /> : (
        <ul className="space-y-2" data-testid="rescue-list">
          {shifts.map((s) => (
            <li key={s._id} className="bg-white border border-red-200 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <p className="font-medium text-slate-900">{s.title}</p>
                  <p className="text-xs text-slate-500">{String(s.start_time).slice(0, 16).replace('T', ' ')} ·
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${STATE_BADGE[s.attendance_state] || 'bg-slate-100'}`}>
                      {(s.attendance_state || 'at risk').replace('_', ' ')}
                    </span>
                    {s.emergency && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white">EMERGENCY</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button onClick={() => findReplacements(s)}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    data-testid={`rescue-find-${s._id}`}>
                    <UserPlus className="w-3.5 h-3.5" /> Find Replacement
                  </button>
                  {!s.emergency && (
                    <button onClick={() => openEmergency(s)}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      data-testid={`rescue-emergency-${s._id}`}>
                      <Megaphone className="w-3.5 h-3.5" /> Open Emergency
                    </button>
                  )}
                  <button onClick={() => resolve(s)}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700"
                    data-testid={`rescue-resolve-${s._id}`}>
                    <XCircle className="w-3.5 h-3.5" /> Resolve
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {replFor && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setReplFor(null)} data-testid="replacements-modal">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-3 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-slate-900">Smart replacements — {replFor.title}</h3>
            {busy ? <SkeletonRows /> : repls.length === 0 ? <EmptyState label="No qualified replacements found. Try Open Emergency." /> : (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl">
                {repls.map((m, i) => (
                  <li key={m.worker_id || i} className="flex items-center justify-between px-4 py-2.5">
                    <div>
                      <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                        {i + 1}. {m.name || m.worker_name || m.email}
                        {m.badge && (
                          <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase ${BADGE_STYLES[m.badge]}`}
                            data-testid={`replacement-badge-${m.worker_id}`}>
                            {m.badge} · {m.streak}🔥
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        score {Math.round(m.score || 0)} · rating {m.rating ?? '—'}
                        {m.streak ? ` · ${m.streak} on-time streak` : ''}
                        {m.no_show_count ? ` · ${m.no_show_count} no-shows` : ' · clean record'}
                      </p>
                    </div>
                    <button onClick={() => assign(m.worker_id)}
                      className="text-xs px-2.5 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                      data-testid={`replacement-assign-${m.worker_id}`}>Assign</button>
                  </li>
                ))}
              </ul>
            )}
            <button onClick={() => setReplFor(null)} className="w-full py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700" data-testid="replacements-modal-close">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AttendanceAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => { (async () => {
    try { setData((await apiCall(`/api/org/attendance/analytics?days=${days}`)).analytics); }
    catch (e) { toast.error(e.message); }
  })(); }, [days]);

  if (!data) return <SkeletonRows />;
  const kpis = [
    ['Attendance rate', `${data.attendance_rate}%`],
    ['Clock-out compliance', `${data.clock_out_compliance}%`],
    ['Late rate', `${data.late_rate}%`],
    ['No-show rate', `${data.no_show_rate}%`],
    ['Rescues activated', data.rescues_activated],
    ['Shifts recovered', data.shifts_recovered],
    ['Recovery rate', `${data.recovery_rate}%`],
    ['Scheduled shifts', data.scheduled_shifts],
  ];
  return (
    <div className="space-y-4" data-testid="attendance-analytics">
      <div className="flex items-center justify-between">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs bg-white" data-testid="analytics-days">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <button onClick={() => downloadFile(`/api/org/attendance/analytics?days=${days}&format=csv`, 'attendance-analytics.csv').then(() => toast.success('CSV downloaded')).catch((e) => toast.error(e.message))}
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700"
          data-testid="analytics-export-csv">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map(([label, value]) => (
          <div key={label} className="bg-white border border-slate-200 rounded-xl p-3">
            <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
            <p className="text-lg font-semibold text-slate-900">{value}</p>
          </div>
        ))}
      </div>
      {(data.workers || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h5 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5"><Users className="w-4 h-4" /> Worker reliability</h5>
          <ul className="divide-y divide-slate-100">
            {data.workers.map((w) => (
              <li key={w.worker_id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-800">{w.name || w.worker_id}</span>
                <span className="text-xs text-slate-500">{w.shifts} shifts · {w.late} late · <span className={w.on_time_rate >= 90 ? 'text-emerald-600 font-semibold' : 'text-amber-600 font-semibold'}>{w.on_time_rate}% on-time</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AttendanceSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    try { setS((await apiCall('/api/org/attendance-settings')).settings); }
    catch (e) { toast.error(e.message); }
  })(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiCall('/api/org/attendance-settings', { method: 'PUT', body: JSON.stringify(s) });
      setS(res.settings);
      toast.success('Attendance settings saved');
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (!s) return <SkeletonRows />;
  const num = (key, label) => (
    <div key={key}>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input type="number" min={0} max={1440} value={s[key] ?? ''} onChange={(e) => setS((v) => ({ ...v, [key]: Number(e.target.value) }))}
        className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid={`att-setting-${key}`} />
    </div>
  );
  const bool = (key, label) => (
    <label key={key} className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={!!s[key]} onChange={(e) => setS((v) => ({ ...v, [key]: e.target.checked }))} data-testid={`att-setting-${key}`} />
      {label}
    </label>
  );
  return (
    <div className="space-y-4 max-w-xl" data-testid="attendance-settings">
      <div className="grid grid-cols-2 gap-3">
        {num('grace_late_minutes', 'Late after (min)')}
        {num('grace_at_risk_minutes', 'At-risk / rescue after (min)')}
        {num('grace_no_show_minutes', 'No-show after (min)')}
        {num('auto_open_emergency_minutes', 'Auto open-emergency after (min)')}
        {num('max_break_minutes', 'Max break duration (min)')}
        {num('break_reminder_hours', 'Remind to take a break after (h, 0 = off)')}
        {num('min_rest_hours', 'Minimum rest between shifts (h)')}
      </div>
      <div className="space-y-2">
        {bool('require_checkin_photo', 'Require check-in selfie (photo verification)')}
        {bool('qr_checkin_enabled', 'Allow QR code check-in')}
        {bool('nfc_checkin_enabled', 'Allow NFC tag check-in')}
      </div>
      <button onClick={save} disabled={saving}
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
        data-testid="att-settings-save">
        {saving ? 'Saving…' : 'Save settings'}
      </button>
      <p className="text-[11px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> The attendance monitor evaluates shifts every 5 minutes using these thresholds.</p>
    </div>
  );
}
